import moment from "moment";
import { SUBDOMAIN_TENANT_MAP } from "./Constants";
import { DEFAULT_TENANT } from "../environment";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getFromLocalStorage = (item: string): string | null => {
  return localStorage.getItem(item);
};

const setInLocalStorage = (key: string, value: string) => {
  localStorage.setItem(key, value);
};

const removeFromLocalStorage = (key: string) => {
  localStorage.removeItem(key);
};

export const getAuthToken = (): string | null => {
  return getFromLocalStorage("token");
};

export const removeRefreshAuthToken = () => {
    removeFromLocalStorage('refresh-token')
}

export const getRefreshAuthToken = (): string | null => {
   return getFromLocalStorage('refresh-token')
}

export const storeUserProfile = (profile: any) => {
  setInLocalStorage('userProfile', JSON.stringify(profile))
}

export const getUserProfile = (): any | null => {
  let text = getFromLocalStorage("userProfile");
  if (text) {
    return JSON.parse(text);
  } else {
    return null;
  }
}

export const removeUserProfile = () => {
  removeFromLocalStorage('userProfile')
}


export const hasRefreshAuthToken = (): boolean => {
    const token = getRefreshAuthToken()
    if(token){
        return true
    } else {
        return false
    }
}

export const setRefreshAuthToken = (token: string) => {
    setInLocalStorage('refresh-token',token)
}

export const getAccess = (): any[] | null => {
  let text = getFromLocalStorage("access");
  if (text) {
    return JSON.parse(text);
  } else {
    return null;
  }
};

export const setAccesss = (modules: any[]) => {
  setInLocalStorage("access", JSON.stringify(modules));
};

export const removeAuthToken = () => {
  removeFromLocalStorage("token");
};

export const removeAccessToken = () => {
  removeFromLocalStorage("access");
};

export const setAuthToken = (token: string) => {
  setInLocalStorage("token", token);
};

export const setTenantID = (tenant: string | undefined) => {
  setInLocalStorage("tenant", tenant || "");
}

export const getTenant = () => {
 return getFromLocalStorage("tenant");
}

export const removeTenantID = () => {
  removeFromLocalStorage("tenant");
}

export const getSubdomain = (hostname?: string): string | null => {
  const host = (
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : "")
  ).toLowerCase();

  if (!host || host === "localhost" || host === "127.0.0.1") {
    return null;
  }

  const parts = host.split(".");

  if (parts.length >= 3) {
    if (parts[0] === "www" && parts.length >= 4) {
      return parts[1];
    }
    return parts[0];
  }

  if (parts.length === 2 && parts[1] === "localhost" && parts[0] !== "www") {
    return parts[0];
  }

  return null;
};

export const getTenantIdForSubdomain = (
  subdomain: string | null
): string | undefined => {
  if (!subdomain) {
    return undefined;
  }
  const tenantId = SUBDOMAIN_TENANT_MAP[subdomain.toLowerCase()];
  return tenantId?.trim() || undefined;
};

const getHostname = (hostname?: string): string => {
  return (
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : "")
  ).toLowerCase();
};

/** Resolves tenant from full hostname, then first subdomain label (e.g. peer0). */
export const getTenantIdForCurrentSubdomain = (hostname?: string): string | undefined => {
  const host = getHostname(hostname);
  if (host) {
    const fromHost = getTenantIdForSubdomain(host);
    if (fromHost) {
      return fromHost;
    }
  }
  return getTenantIdForSubdomain(getSubdomain(host || undefined));
};

export const isTenantLockedBySubdomain = (): boolean => {
  return !!getTenantIdForCurrentSubdomain();
};

export const resolveAuthTenantId = (urlTenantParam?: string | null): {
  tenantId: string | undefined;
  fromSubdomain: boolean;
} => {
  const subdomainTenant = getTenantIdForCurrentSubdomain();
  if (subdomainTenant) {
    return { tenantId: subdomainTenant, fromSubdomain: true };
  }

  const fromUrl = urlTenantParam?.trim();
  if (fromUrl) {
    return { tenantId: fromUrl, fromSubdomain: false };
  }

  const stored = getTenant()?.trim();
  if (stored) {
    return { tenantId: stored, fromSubdomain: false };
  }

  const defaultTenant = DEFAULT_TENANT?.trim();
  return { tenantId: defaultTenant || undefined, fromSubdomain: false };
};

export const syncSubdomainTenantToLocalStorage = (): void => {
  const subdomainTenant = getTenantIdForCurrentSubdomain();
  if (subdomainTenant) {
    setTenantID(subdomainTenant);
  }
};

export const setRememberMe = (remember: boolean) => {
  if (remember) {
    setInLocalStorage("rememberMe", "true");
  } else {
    removeFromLocalStorage("rememberMe");
  }
}

export const getRememberMe = (): boolean => {
  return getFromLocalStorage("rememberMe") === "true";
};

export const getAPIHeaders = () => {
  return {
    "Content-Type": "application/json",
    Authorization: getAuthToken(),
    "X-Tenant-ID": getTenant(),
  };
};

export const getAPIMultiPartHeaders = () => {
  return {
    "Content-Type": "multipart/form-data",
    Authorization: getAuthToken(),
    "X-Tenant-ID": getTenant(),
  };
};

export const getDayMonthTime = (epochDt: number): string => {
  if (epochDt) {
    var dt = new Date(epochDt * 1000);

    // dt.getDate() + ((dt.getDate()-1)%10 == 0 ? 'st': ((dt.getDate()-2)%10 == 0) ? 'nd' : ((dt.getDate()-3)%10 == 0) ? 'rd' : 'th' ) + ' ' +
    return (
      monthNames[dt.getMonth()]?.substr(0, 3) +
      " " +
      dt.getDate() +
      ", " +
      dt.getHours() +
      ":" +
      (dt.getMinutes() < 10 ? "0" : "") +
      dt.getMinutes()
    );
  }
  return "";
};

export const durationInHoursMins = (duration: number): string => {
  if (duration) {
    var hrs = Math.floor(duration / 3600);
    var mins = Math.floor((duration - hrs * 3600) / 60);
    return hrs + " hrs " + mins + " mins";
  }

  return "";
};

export function getFormattedDateString(epochDate: number | undefined) {
  let date = toDateFromEpoch(epochDate);
  if (date) {
    return moment(date).format("Do MMM YYYY, h:mm:ss A");
  } else {
    return "";
  }
}

export function getFormattedDateDDMMYYYY(epochDate: number | undefined) {
  let date = toDateFromEpoch(epochDate);
  if (date) {
    return `${
      date.getUTCDate() <= 9 ? "0" + date.getUTCDate() : date.getUTCDate()
    }-${
      date.getUTCMonth() <= 8
        ? "0" + (date.getUTCMonth() + 1)
        : date.getUTCMonth() + 1
    }-${date.getFullYear()}`;
  } else {
    return "";
  }
}

export function toDateFromEpoch(epochDate: number | undefined) {
  if (epochDate) {
    let dateEpochMilliSec = epochDate;
    if (epochDate?.toString().length <= 10) {
      dateEpochMilliSec = epochDate * 1000;
    }
    var date = new Date(0);
    date.setUTCMilliseconds(
      dateEpochMilliSec + date.getTimezoneOffset() * -60000
    );
    return date;
  } else {
    return null;
  }
}

// export function convertYYYYMMDDToEpochDate(dateString: string) {
//   if (dateString) {
//     let d = dateString.split("-");
//     let date = new Date(parseInt(d[0]), +(parseInt(d[1]) - 1), parseInt(d[2]));
//     return date.getTime();
//   } else {
//     return null;
//   }
// }

export const isCurrentDateInBetween = (
  startEpochDt?: number,
  endEpochDt?: number
) => {
  if (startEpochDt && endEpochDt) {
    const startDate = moment(toDateFromEpoch(startEpochDt)).startOf("day");
    const endDate = moment(toDateFromEpoch(endEpochDt)).startOf("day");
    const currentDate = moment().startOf("day");
    return currentDate.isBetween(startDate, endDate, null, "[]");
  } else {
    return false;
  }
};

export const isBeforeCurrentDate = (epochDt?: number) => {
  if (epochDt) {
    const date = moment(toDateFromEpoch(epochDt)).startOf("day");
    const currentDate = moment().startOf("day");
    return date.isBefore(currentDate);
  } else {
    return false;
  }
};

export function toCamelCase(sentence: string | undefined) {
  return sentence
    ? sentence
        .toLowerCase()
        .split(" ")
        .map((word, index) => {
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join("")
    : sentence;
}

export const isValidPhoneNumber = (phoneNumber: any): boolean => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phoneNumber);
};

export const isValidEmail = (email: string | undefined): boolean => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isNotEmpty = (value: any | undefined): boolean => {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  if (Array.isArray(value)) {
    return value?.length > 0;
  }
  return true;
}



/**
 * Formats file size in a human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Validates if a file is an Excel file (.xlsx or .xls)
 */
export const isExcelFile = (file: File): boolean => {
  const validTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.ms-excel", // .xls
  ];
  return validTypes.includes(file.type);
};

/**
 * Validates file size (max 10MB)
 */
export const isValidFileSize = (file: File, maxSizeMB = 10): boolean => {
  const maxSize = maxSizeMB * 1024 * 1024; // Convert to bytes
  return file.size <= maxSize;
};



export function convertYYYYMMDDToEpochDate(dateString: string) {
    if (!dateString) return null;
    
    try {
        // Handle both slash and hyphen formats
        const separator = dateString.includes('/') ? '/' : dateString.includes('-') ? '-' : null;
        
        // If no valid separator is found
        if (!separator) return null;
        
        const d = dateString.split(separator);
        if (d.length !== 3) return null;
        
        // Parse with explicit radix
        const year = parseInt(d[0], 10);
        const month = parseInt(d[1], 10) - 1; // Months are 0-indexed in JS
        const day = parseInt(d[2], 10);
        
        // Set time to noon UTC to avoid timezone issues
        const date = new Date(Date.UTC(year, month, day, 12, 0, 0));
        
        // Validate that the date is valid
        if (isNaN(date.getTime())) return null;
        
        return date.getTime();
    } catch (error) {
        console.error("Error converting YYYY/MM/DD date:", error, dateString);
        return null;
    }
}

export function convertDDMMYYYYToEpochDate(dateString: string) {
    if (!dateString) return null;
    try {
        // Handle both slash and hyphen formats
        const separator = dateString.includes('/') ? '/' : dateString.includes('-') ? '-' : null;
        
        // If no valid separator is found
        if (!separator) return null;
        
        const d = dateString.split(separator);
        if (d.length !== 3) return null;
        
        // Parse with explicit radix
        const year = parseInt(d[2], 10);
        const month = parseInt(d[1], 10) - 1; // Months are 0-indexed in JS
        const day = parseInt(d[0], 10);
        
        // Set time to noon UTC to avoid timezone issues
        const date = new Date(Date.UTC(year, month, day, 12, 0, 0));
        
        // Validate that the date is valid
        if (isNaN(date.getTime())) return null;

        return date.getTime();
    } catch (error) {
        console.error("Error converting DD/MM/YYYY date:", error, dateString);
        return null;
    }
}